import { useState } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useAcknowledgeImport,
  useImportAlert,
  useRetryImport,
} from '@/hooks/useGit';
import { useUserRoles } from '@/hooks/useUserRoles';
import { toastService } from '@/services/toast.service';

/**
 * A bar across the top of the app when an import from git failed.
 *
 * An import failure is not an ordinary background error: it means Pliable and
 * the repository now disagree about what the project is, and neither side knows
 * it. Someone in the app is looking at models that may no longer match the
 * branch, and until it is fixed every later sync is reasoning about stale state.
 * That is worth interrupting for, so it is a bar rather than a toast — a toast
 * is gone in five seconds and the disagreement is not.
 *
 * Only Admins and Builders see it. A ReadOnly user cannot re-run an import or
 * fix a branch mapping, so for them this is an alarm with no switch.
 */
export default function ImportFailureBanner() {
  const { isAdminOrBuilder } = useUserRoles();
  const { data } = useImportAlert(isAdminOrBuilder);
  const acknowledge = useAcknowledgeImport();
  const retry = useRetryImport();
  const [open, setOpen] = useState(false);

  const alert = data?.alert;
  if (!alert) return null;

  const failed = alert.status === 'FAILED';
  const detail = failed ? alert.error_message : alert.skipped_reason;

  const onRetry = async () => {
    try {
      await retry.mutateAsync(alert.id);
      toastService.success('Import re-queued. The banner clears once it succeeds.');
      setOpen(false);
    } catch (e) {
      toastService.error(e instanceof Error ? e.message : 'Could not start the import.');
    }
  };

  const onDismiss = async () => {
    try {
      await acknowledge.mutateAsync(alert.id);
      setOpen(false);
    } catch {
      // Dismissing is a convenience; failing to dismiss is not worth a toast.
    }
  };

  return (
    <>
      <div
        role="alert"
        className={
          'flex items-center gap-3 px-4 py-2 text-sm border-b ' +
          (failed
            ? 'bg-destructive/10 border-destructive/30 text-destructive'
            : 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400')
        }
      >
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">
          {alert.summary} Pliable and git may be out of sync.
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 shrink-0 underline"
          onClick={() => setOpen(true)}
        >
          Details
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          aria-label="Dismiss"
          onClick={onDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {failed ? 'Import failed' : 'A change was not imported'}
            </DialogTitle>
            <DialogDescription>
              {failed
                ? 'Pliable could not read the project from git, so what you see here may not match the repository.'
                : 'A push reached Pliable but nothing was imported from it.'}
            </DialogDescription>
          </DialogHeader>

          <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm">
            {alert.repo && (
              <>
                <dt className="text-muted-foreground">Repository</dt>
                <dd className="font-mono">
                  {alert.repo}
                  {alert.git_branch ? `#${alert.git_branch}` : ''}
                </dd>
              </>
            )}
            {alert.commit_sha && (
              <>
                <dt className="text-muted-foreground">Commit</dt>
                <dd className="font-mono">{alert.commit_sha.slice(0, 10)}</dd>
              </>
            )}
            <dt className="text-muted-foreground">Into</dt>
            <dd>{alert.target_branch === 'PROD' ? 'Production' : 'Develop'}</dd>
            <dt className="text-muted-foreground">Started by</dt>
            <dd>
              {alert.trigger === 'WEBHOOK' ? 'A push to git' : alert.triggered_by || alert.trigger}
            </dd>
            {alert.started_at && (
              <>
                <dt className="text-muted-foreground">When</dt>
                <dd>{new Date(alert.started_at).toLocaleString()}</dd>
              </>
            )}
          </dl>

          {detail && (
            <div className="mt-2">
              <p className="text-sm font-medium mb-1">
                {failed ? 'Error' : 'Reason'}
              </p>
              <pre className="max-h-64 overflow-auto rounded bg-muted p-3 text-xs whitespace-pre-wrap break-words">
                {detail}
              </pre>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={onDismiss} disabled={acknowledge.isPending}>
              Dismiss
            </Button>
            {alert.can_retry && (
              <Button onClick={onRetry} disabled={retry.isPending}>
                {retry.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Run the import again
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
